import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api";
import { Clinician } from "../../types";
import { asArray, isRecord } from "../payloadGuards";

interface ClinicState {
    clinicians: Clinician[];
    practiceClinicians: Clinician[];
    practiceCliniciansPagination: {
        count: number;
        page?: number;
        limit?: number;
        totalPages?: number;
        hasNextPage?: boolean;
        hasPrevPage?: boolean;
    } | null;
    /** From endpoint: { active, inactive, total } */
    practiceCliniciansCounts: { active: number; inactive: number; total: number } | null;
    selectedClinician: Clinician | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    isLoading: boolean;
    // Add bulk update specific state
    bulkUpdate: {
        isLoading: boolean;
        success: boolean;
        error: string | null;
        data: { updated: string[]; not_updated: string[] } | null;
    };
    // Add analytics state
    analytics: {
        data: {
            data: Array<{
                clinicianId: string;
                name: string;
                title: string;
                specialty: string;
                metrics: {
                    profileViews: {
                        total: number;
                        trend: string;
                        trendValue: number;
                    };
                    profileClicks: {
                        total: number;
                        trend: string;
                        trendValue: number;
                    };
                    connectRequests: {
                        total: number;
                        trend: string;
                        trendValue: number;
                    };
                    conversionRates: {
                        clickConversion: string;
                        connectionConversion: string;
                        overallConversion: string;
                    };
                };
            }>;
            metadata: {
                dataPoints: {
                    views: number;
                    clicks: number;
                    connections: number;
                };
                generatedAt: string;
                reportType: string;
            };
            success: boolean;
            summary: {
                totalClinicians: number;
                totalViews: number;
                totalClicks: number;
                totalConnections: number;
                averageConversionRate: string;
            };
        } | null;
        loading: boolean;
        error: string | null;
    };
    // Activate clinicians state
    activateClinicians: {
        isLoading: boolean;
        activated: number | null;
        error: string | null;
    };
}

interface BulkUpdatePayload {
    clinicians: Array<{
        id: string;
        status?: 'active' | 'inactive' | 'pending';
        ageRange?: { min?: number; max?: number };
        location?: { city?: string; state?: string; zipCode?: string };
        insurance?: string[];
        [key: string]: any; // Allow additional fields
    }>;
    body?: {
        enabled?: boolean;
        isActive?: boolean;
        clinicianId?: string;
        verificationStatus?: 'unverified' | 'pending' | 'verified';
        billingStatus?: 'pending' | 'active' | 'suspended';
        [key: string]: string | number | boolean | undefined;
    };
}

const initialState: ClinicState = {
    clinicians: [],
    practiceClinicians: [],
    practiceCliniciansPagination: null,
    practiceCliniciansCounts: null,
    selectedClinician: null,
    status: 'idle',
    error: null,
    isLoading: false,
    bulkUpdate: {
        isLoading: false,
        success: false,
        error: null,
        data: null,
    },
    analytics: {
        data: null,
        loading: false,
        error: null,
    },
    activateClinicians: {
        isLoading: false,
        activated: null,
        error: null,
    },
};

export const fetchClinicians = createAsyncThunk(
    'clinics/fetchClinicians',
    async () => {

        try {
            const response = await api.get('/clinician');
            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to fetch clinicians";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);


export const fetchCliniciansDetails = createAsyncThunk(
    'clinics/fetchCliniciansDetails',
    async (id: string) => {
        try {
            const response = await api.get(`/clinician/${id}`);

            // Handle different response structures
            let data: any;

            // Case 1: response.data.body is a JSON string (Lambda API Gateway format)
            if (response.data?.body && typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (parseError) {
                    console.error('❌ Error parsing details body string:', parseError);
                    data = response.data.body;
                }
            }
            // Case 2: response.data.body is already an object
            else if (response.data?.body && typeof response.data.body === 'object') {
                data = response.data.body;
            }
            // Case 3: response.data is the direct response
            else {
                data = response.data;
            }

            return data;
        } catch (error) {
            let errorMessage = "Failed to fetch clinician details";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const fetchCliniciansByPracticeId = createAsyncThunk(
    'clinics/fetchCliniciansByPracticeId',
    async (payload: {
        practiceId: string;
        isActive?: boolean;
        page?: number;
        limit?: number;
        search?: string;
        specialty?: string;
        location?: string | string[];
        sortBy?: string;
    }) => {
        const practiceId = typeof payload === 'string' ? payload : payload.practiceId;
        const isActive = typeof payload === 'string' ? undefined : payload.isActive;
        const page = typeof payload === 'string' ? undefined : payload.page;
        const limit = typeof payload === 'string' ? undefined : payload.limit;
        const search = typeof payload === 'string' ? undefined : payload.search;
        const specialty = typeof payload === 'string' ? undefined : payload.specialty;
        const location = typeof payload === 'string' ? undefined : payload.location;
        const sortBy = typeof payload === 'string' ? undefined : payload.sortBy;
        try {
            const params: Record<string, string | number | boolean | string[]> = {};
            if (isActive !== undefined) params.isActive = isActive;
            if (page != null && page > 0) params.page = page;
            if (limit != null && limit > 0) params.limit = limit;
            if (search) params.search = search;
            if (specialty) params.specialty = specialty;
            if (location) {
                params.location = Array.isArray(location) ? location.filter(Boolean) : [location];
            }
            if (sortBy) params.sortBy = sortBy;
            const config = Object.keys(params).length
                ? {
                    params,
                    paramsSerializer: (p: Record<string, string | number | boolean | string[]>) => {
                        const sp = new URLSearchParams();
                        Object.entries(p).forEach(([key, value]) => {
                            if (Array.isArray(value)) {
                                value.forEach((v) => sp.append(key, String(v)));
                            } else {
                                sp.append(key, String(value));
                            }
                        });
                        return sp.toString();
                    }
                }
                : {};
            const response = await api.get(`/practice/${practiceId}/clinicians`, config);
            const parseMaybeJson = (value: unknown): unknown => {
                if (typeof value !== 'string') return value;
                try {
                    return JSON.parse(value);
                } catch {
                    return value;
                }
            };

            const firstParsed = parseMaybeJson(response.data);
            const unwrapped = (
                firstParsed &&
                typeof firstParsed === 'object' &&
                'body' in (firstParsed as Record<string, unknown>)
            )
                ? parseMaybeJson((firstParsed as Record<string, unknown>).body)
                : firstParsed;

            if (Array.isArray(unwrapped)) {
                return {
                    data: unwrapped as Clinician[],
                    counts: { total: unwrapped.length },
                    pagination: {
                        currentPage: page ?? 1,
                        pageSize: limit ?? unwrapped.length,
                        totalItems: unwrapped.length,
                        totalPages: 1,
                        hasNextPage: false,
                        hasPreviousPage: (page ?? 1) > 1
                    }
                };
            }

            if (unwrapped && typeof unwrapped === 'object') {
                const payloadObj = unwrapped as Record<string, unknown>;

                const rawData =
                    payloadObj.data ??
                    payloadObj.results ??
                    payloadObj.clinicians ??
                    payloadObj.items;

                const data = Array.isArray(rawData)
                    ? (rawData as Clinician[])
                    : Array.isArray((rawData as { items?: unknown[] })?.items)
                        ? ((rawData as { items: Clinician[] }).items)
                        : [];

                const counts = (
                    payloadObj.counts &&
                    typeof payloadObj.counts === 'object'
                )
                    ? (payloadObj.counts as Record<string, number>)
                    : {
                        total: Number(payloadObj.total ?? data.length),
                        active: Number(payloadObj.active ?? 0),
                        inactive: Number(payloadObj.inactive ?? 0)
                    };

                const pagination = (
                    payloadObj.pagination &&
                    typeof payloadObj.pagination === 'object'
                )
                    ? (payloadObj.pagination as Record<string, unknown>)
                    : {
                        currentPage: Number(payloadObj.page ?? page ?? 1),
                        pageSize: Number(payloadObj.limit ?? limit ?? data.length),
                        totalItems: Number(payloadObj.total ?? data.length),
                        totalPages: Number(payloadObj.totalPages ?? 1),
                        hasNextPage: Boolean(payloadObj.hasNextPage ?? false),
                        hasPreviousPage: Boolean(payloadObj.hasPrevPage ?? ((Number(payloadObj.page ?? page ?? 1)) > 1))
                    };

                return { data, counts, pagination };
            }

            return {
                data: [],
                counts: { total: 0, active: 0, inactive: 0 },
                pagination: {
                    currentPage: page ?? 1,
                    pageSize: limit ?? 0,
                    totalItems: 0,
                    totalPages: 1,
                    hasNextPage: false,
                    hasPreviousPage: false
                }
            };
        } catch (error) {
            let errorMessage = "Failed to fetch clinician by practice";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const createClinician = createAsyncThunk(
    'clinics/createClinician',
    async (clinicianData: Clinician) => {
        try {
            const response = await api.post('/clinician', clinicianData);
            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to create clinician";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

// Fixed bulk update thunk with proper error handling
export const bulkUpdate = createAsyncThunk(
    'clinics/bulkUpdate',
    async (clinicianData: BulkUpdatePayload) => {
        try {
            // Use POST /clinician/bulk-save endpoint for bulk updates
            const response = await api.post('/clinician/bulk-save', {
                clinicians: clinicianData.clinicians
            });
            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to bulk update clinicians";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const syncCliniciansFromSimplePractice = createAsyncThunk(
    'clinics/syncCliniciansFromSimplePractice',
    async (practiceId: string) => {
        try {
            const response = await api.post(`/practice/${practiceId}/sync-clinicians`, {
                provider: 'simple_practice'
            });
            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to sync clinicians from SimplePractice";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const saveAllClinicians = createAsyncThunk(
    'clinics/saveAllClinicians',
    async (clinicians: Clinician[]) => {
        try {
            const response = await api.post('/clinician/bulk-save', { clinicians });
            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to save all clinicians";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const enableAllClinicians = createAsyncThunk(
    'clinics/enableAllClinicians',
    async (practiceId: string) => {
        try {
            const response = await api.patch('/clinician/enable-all', { practiceId });
            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to enable all clinicians";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const fetchSimplePracticeClinicians = createAsyncThunk(
    'clinics/fetchSimplePracticeClinicians',
    async () => {

        const practiceId = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];

        if (!practiceId) {
            throw new Error('Practice ID not found');
        }
        const params = { practiceId };
        try {
            const response = await api.get('/simplepractice/clinicians', { params });
            // Handle different response structures
            const responseData = response.data?.body || response.data;
            return responseData;
        } catch (error) {
            let errorMessage = "Failed to fetch SimplePractice clinicians";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

// Single clinician status toggle
export const toggleClinicianStatus = createAsyncThunk(
    'clinics/toggleClinicianStatus',
    async ({ clinicianId, isActive }: { clinicianId: string; isActive: boolean }) => {
        try {
            // Try the main clinician endpoint with ID in body
            const response = await api.patch(`/clinician/${clinicianId}/status`, {
                clinicianId,
                isActive,
                status: isActive ? 'active' : 'inactive'
            });
            // Handle 204 No Content or responses without a body field gracefully
            return response.data?.body ?? response.data ?? null;
        } catch (error) {
            console.error('Toggle status error:', error);
            let errorMessage = "Failed to toggle clinician status";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

// Update individual clinician
export const updateClinician = createAsyncThunk(
    'clinics/updateClinician',
    async ({ clinicianId, updateData }: { clinicianId: string; updateData: any }) => {
        try {
            // Send the data in the format expected by the backend
            // Backend expects: { clinicianId, field1, field2, ... }
            const requestData = {
                clinicianId,
                ...updateData
            };


            const response = await api.patch(`/clinician`, requestData);
            return response.data.body;
        } catch (error) {
            console.error('Update clinician error:', error);
            let errorMessage = "Failed to update clinician";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

export const fetchClinicianBySlug = createAsyncThunk(
    'clinics/fetchClinicianBySlug',
    async (slug: string) => {
        try {
            const response = await api.get(`/clinician/public/${slug}`);
            return response.data.body;
        } catch (error) {
            let errorMessage = "Failed to fetch clinician by slug";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

// New endpoint for clinician profile analytics
export const fetchClinicianProfileAnalytics = createAsyncThunk(
    'clinics/fetchClinicianProfileAnalytics',
    async ({ practiceId, timeStart, timeEnd }: { practiceId: string; timeStart: number; timeEnd: number }) => {
        try {
            const response = await api.post(`/practice/${practiceId}/analytics`, {
                time_start: timeStart,
                time_end: timeEnd
            });


            // Handle different response formats
            let data;
            if (typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (parseError) {
                    console.error('Failed to parse response.body as JSON:', parseError);
                    throw new Error('Invalid JSON response from server');
                }
            } else {
                data = response.data.body || response.data;
            }

            return data;
        } catch (error) {
            console.error('API call failed:', error);
            let errorMessage = "Failed to fetch clinician profile analytics";
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

// Simple Practice invites (single)
export const inviteSimplePracticeClinician = createAsyncThunk(
    'clinics/inviteSimplePracticeClinician',
    async ({ clinicianId, email }: { clinicianId: string; email?: string }) => {
        const response = await api.post('/simplepractice/invite', { clinicianId, email });
        return response.data.body || response.data;
    }
);

// Simple Practice invites (bulk)
export const inviteSimplePracticeClinicianBulk = createAsyncThunk(
    'clinics/inviteSimplePracticeClinicianBulk',
    async ({ clinicianIds }: { clinicianIds: string[] }) => {
        const response = await api.post('/simplepractice/invite-bluk', { clinicianIds });
        return response.data.body || response.data;
    }
);

// Bulk invitation with payment processing
export const sendBulkInvitation = createAsyncThunk(
    'clinics/sendBulkInvitation',
    async ({ clinician_ids, paymentMethodId, practice_id, couponCode }: {
        clinician_ids: string[];
        paymentMethodId: string;
        practice_id: string;
        couponCode?: string;
    }) => {
        try {
            const response = await api.post('/invitation/simplepractice/bulk', {
                clinician_ids,
                paymentMethodId,
                practice_id,
                ...(couponCode ? { couponCode } : {}),
            });
            return response.data;
        } catch (error: any) {
            let errorMessage = "Failed to send bulk invitations";
            if (error.response?.data?.msg) {
                errorMessage = error.response.data.msg;
            } else if (error.message) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    }
);

// Activate clinicians after successful payment
export interface ActivateCliniciansPayload {
    transactionId: string; // From payment intent response
    subscriptionId?: string; // From Stripe (optional)
    paymentIntentId: string; // From Stripe
}

export interface ActivateCliniciansResponse {
    results: {
        activated: number;
        failed?: number;
        errors?: string[];
    };
    message?: string;
}

export const activateClinicians = createAsyncThunk<ActivateCliniciansResponse, ActivateCliniciansPayload>(
    'clinics/activateClinicians',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await api.post('/clinician/activate-clinicians', {
                transactionId: payload.transactionId,
                subscriptionId: payload.subscriptionId,
                paymentIntentId: payload.paymentIntentId,
            });


            // Handle different response structures
            let data: any;

            // Case 1: response.data.body is a JSON string (Lambda API Gateway format)
            if (response.data?.body && typeof response.data.body === 'string') {
                try {
                    data = JSON.parse(response.data.body);
                } catch (parseError) {
                    console.error('❌ Error parsing body string:', parseError);
                    // Fallback to using body as-is if parsing fails
                    data = response.data.body;
                }
            }
            // Case 2: response.data.body is already an object
            else if (response.data?.body && typeof response.data.body === 'object') {
                data = response.data.body;
            }
            // Case 3: response.data is the direct response
            else {
                data = response.data;
            }


            return data as ActivateCliniciansResponse;
        } catch (error: any) {
            console.error('❌ Error activating clinicians:', error);
            const errorMessage = error.response?.data?.message
                || error.response?.data?.error
                || error.message
                || 'Failed to activate clinicians';
            return rejectWithValue(errorMessage);
        }
    }
);

const clinicSlice = createSlice({
    name: 'clinics',
    initialState,
    reducers: {
        // Add reducer to clear bulk update state
        clearBulkUpdateState: (state) => {
            state.bulkUpdate = {
                isLoading: false,
                success: false,
                error: null,
                data: null,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // Existing cases...
            .addCase(fetchClinicians.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchClinicians.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.clinicians = asArray(action.payload);
                state.isLoading = false;
            })
            .addCase(fetchClinicians.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Unknown error occurred';
                state.isLoading = false;
            })
            .addCase(fetchCliniciansDetails.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
                state.selectedClinician = null;
            })
            .addCase(fetchCliniciansDetails.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.selectedClinician = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchCliniciansDetails.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Unknown error occurred';
                state.isLoading = false;
                state.selectedClinician = null;
            })
            .addCase(createClinician.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(createClinician.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (isRecord(action.payload)) state.clinicians.push(action.payload);
                state.isLoading = false;
            })
            .addCase(createClinician.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Unknown error occurred';
                state.isLoading = false;
            })
            .addCase(fetchCliniciansByPracticeId.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchCliniciansByPracticeId.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const payload = action.payload;
                // New API shape: { data: clinician[], counts: {...}, pagination: {...} }
                if (payload && typeof payload === 'object' && !Array.isArray(payload) && 'data' in payload && Array.isArray(payload.data)) {
                    state.practiceClinicians = payload.data;
                    const counts = payload.counts as { active?: number; inactive?: number; total?: number } | undefined;
                    state.practiceCliniciansCounts = counts && (typeof counts.active === 'number' || typeof counts.inactive === 'number' || typeof counts.total === 'number')
                        ? { active: counts.active ?? 0, inactive: counts.inactive ?? 0, total: counts.total ?? 0 }
                        : null;
                    const pag = payload.pagination as { currentPage?: number; pageSize?: number; totalItems?: number; totalPages?: number; hasNextPage?: boolean; hasPreviousPage?: boolean } | undefined;
                    if (pag) {
                        state.practiceCliniciansPagination = {
                            count: counts?.total ?? pag.totalItems ?? payload.data.length,
                            page: pag.currentPage,
                            limit: pag.pageSize,
                            totalPages: pag.totalPages,
                            hasNextPage: pag.hasNextPage,
                            hasPrevPage: pag.hasPreviousPage,
                        };
                    } else {
                        state.practiceCliniciansPagination = {
                            count: counts?.total ?? payload.data.length,
                            page: 1,
                            limit: payload.data.length,
                            totalPages: 1,
                            hasNextPage: false,
                            hasPrevPage: false,
                        };
                    }
                } else if (Array.isArray(payload)) {
                    state.practiceClinicians = payload;
                    state.practiceCliniciansCounts = null;
                    state.practiceCliniciansPagination = {
                        count: payload.length,
                        page: 1,
                        limit: payload.length,
                        totalPages: 1,
                        hasNextPage: false,
                        hasPrevPage: false,
                    };
                } else {
                    state.practiceClinicians = [];
                    state.practiceCliniciansCounts = null;
                    state.practiceCliniciansPagination = null;
                }
                state.isLoading = false;
            })
            .addCase(fetchCliniciansByPracticeId.rejected, (state) => {
                state.status = 'failed';
                state.isLoading = false;
                state.practiceCliniciansCounts = null;
            })
            // Add bulk update cases
            .addCase(bulkUpdate.pending, (state) => {
                state.bulkUpdate.isLoading = true;
                state.bulkUpdate.success = false;
                state.bulkUpdate.error = null;
                state.bulkUpdate.data = null;
            })
            .addCase(bulkUpdate.fulfilled, (state, action) => {
                state.bulkUpdate.isLoading = false;
                state.bulkUpdate.success = true;
                state.bulkUpdate.error = null;
                state.bulkUpdate.data = action.payload;
            })
            .addCase(bulkUpdate.rejected, (state, action) => {
                state.bulkUpdate.isLoading = false;
                state.bulkUpdate.success = false;
                state.bulkUpdate.error = action.error.message || 'Failed to bulk update clinicians';
                state.bulkUpdate.data = null;
            })
            // Add save all clinicians cases
            .addCase(saveAllClinicians.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(saveAllClinicians.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Update clinicians if the response includes updated data
                if (action.payload?.clinicians) {
                    state.clinicians = action.payload.clinicians;
                }
            })
            .addCase(saveAllClinicians.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to save all clinicians';
                state.isLoading = false;
            })
            // Add enable all clinicians cases
            .addCase(enableAllClinicians.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(enableAllClinicians.fulfilled, (state) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Update all clinicians to enabled status
                state.clinicians = state.clinicians.map(clinician => ({
                    ...clinician,
                    enabled: true
                }));
                // Add array check before mapping practiceClinicians
                if (Array.isArray(state.practiceClinicians)) {
                    state.practiceClinicians = state.practiceClinicians.map(clinician => ({
                        ...clinician,
                        enabled: true
                    }));
                }
            })
            .addCase(enableAllClinicians.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to enable all clinicians';
                state.isLoading = false;
            })
            // Add single clinician status toggle cases
            .addCase(toggleClinicianStatus.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(toggleClinicianStatus.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Update the specific clinician's status
                const updatedClinician = action.payload;
                if (updatedClinician && updatedClinician._id) {
                    state.clinicians = state.clinicians.map(clinician =>
                        clinician._id === updatedClinician._id
                            ? { ...clinician, isActive: updatedClinician.isActive, enabled: updatedClinician.isActive }
                            : clinician
                    );
                    if (Array.isArray(state.practiceClinicians)) {
                        state.practiceClinicians = state.practiceClinicians.map(clinician =>
                            clinician._id === updatedClinician._id
                                ? { ...clinician, isActive: updatedClinician.isActive, enabled: updatedClinician.isActive }
                                : clinician
                        );
                    }
                }
            })
            .addCase(toggleClinicianStatus.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to toggle clinician status';
                state.isLoading = false;
            })
            .addCase(updateClinician.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(updateClinician.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Update the specific clinician's status
                const updatedClinician = action.payload;
                if (updatedClinician && updatedClinician._id) {
                    state.clinicians = state.clinicians.map(clinician =>
                        clinician._id === updatedClinician._id
                            ? { ...clinician, ...updatedClinician }
                            : clinician
                    );
                    if (Array.isArray(state.practiceClinicians)) {
                        state.practiceClinicians = state.practiceClinicians.map(clinician =>
                            clinician._id === updatedClinician._id
                                ? { ...clinician, ...updatedClinician }
                                : clinician
                        );
                    }
                }
            })
            .addCase(updateClinician.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to update clinician';
                state.isLoading = false;
            })
            .addCase(fetchClinicianBySlug.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchClinicianBySlug.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.selectedClinician = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchClinicianBySlug.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to fetch clinician by slug';
                state.isLoading = false;
            })
            .addCase(fetchSimplePracticeClinicians.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(fetchSimplePracticeClinicians.fulfilled, (state, action) => {
                state.status = 'succeeded';

                let cliniciansData: any[] = [];
                let paginationData: any = null;

                // Handle response format: {practiceId: "...", total: 42, clinicians: [...]} or {count: 42, data: [...], page: 1, limit: 10, totalPages: 5} or direct array
                if (action.payload && typeof action.payload === 'object') {
                    // Check if payload has a 'clinicians' property that is an array (new format)
                    if ('clinicians' in action.payload && Array.isArray(action.payload.clinicians)) {
                        cliniciansData = action.payload.clinicians;
                        paginationData = {
                            count: action.payload.total ?? action.payload.clinicians.length,
                            page: action.payload.page || 1,
                            limit: action.payload.limit || action.payload.clinicians.length,
                            totalPages: action.payload.totalPages || 1,
                            hasNextPage: action.payload.hasNextPage || false,
                            hasPrevPage: action.payload.hasPrevPage || false,
                        };
                    }
                    // Check if payload has a 'data' property that is an array (old format)
                    else if ('data' in action.payload && Array.isArray(action.payload.data)) {
                        cliniciansData = action.payload.data;
                        paginationData = {
                            count: action.payload.count ?? action.payload.data.length,
                            page: action.payload.page,
                            limit: action.payload.limit,
                            totalPages: action.payload.totalPages,
                            hasNextPage: action.payload.hasNextPage,
                            hasPrevPage: action.payload.hasPrevPage,
                        };
                    }
                    // Check if payload itself is an array
                    else if (Array.isArray(action.payload)) {
                        cliniciansData = action.payload;
                        paginationData = {
                            count: action.payload.length,
                            page: 1,
                            limit: action.payload.length,
                            totalPages: 1,
                            hasNextPage: false,
                            hasPrevPage: false,
                        };
                    }
                    // Check if payload has nested structure (e.g., { body: { clinicians: [...] } or { body: { data: [...] } })
                    else if ('body' in action.payload && action.payload.body) {
                        const body = action.payload.body;
                        if (Array.isArray(body)) {
                            cliniciansData = body;
                            paginationData = {
                                count: body.length,
                                page: 1,
                                limit: body.length,
                                totalPages: 1,
                                hasNextPage: false,
                                hasPrevPage: false,
                            };
                        } else if (body && typeof body === 'object') {
                            // Check for 'clinicians' array in body (new format)
                            if ('clinicians' in body && Array.isArray(body.clinicians)) {
                                cliniciansData = body.clinicians;
                                paginationData = {
                                    count: body.total ?? body.clinicians.length,
                                    page: body.page || 1,
                                    limit: body.limit || body.clinicians.length,
                                    totalPages: body.totalPages || 1,
                                    hasNextPage: body.hasNextPage || false,
                                    hasPrevPage: body.hasPrevPage || false,
                                };
                            }
                            // Check for 'data' array in body (old format)
                            else if ('data' in body && Array.isArray(body.data)) {
                                cliniciansData = body.data;
                                paginationData = {
                                    count: body.count ?? body.data.length,
                                    page: body.page,
                                    limit: body.limit,
                                    totalPages: body.totalPages,
                                    hasNextPage: body.hasNextPage,
                                    hasPrevPage: body.hasPrevPage,
                                };
                            }
                        }
                    }
                }

                // Set the state
                if (cliniciansData.length > 0) {
                    state.practiceClinicians = cliniciansData;
                    state.practiceCliniciansPagination = paginationData;
                } else {
                    state.practiceClinicians = [];
                    state.practiceCliniciansPagination = null;
                }

                state.isLoading = false;
            })
            .addCase(fetchSimplePracticeClinicians.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to fetch SimplePractice clinicians';
                state.isLoading = false;
                state.practiceCliniciansPagination = null;
            })
            // Add analytics cases
            .addCase(fetchClinicianProfileAnalytics.pending, (state) => {
                state.analytics.loading = true;
                state.analytics.error = null;
            })
            .addCase(fetchClinicianProfileAnalytics.fulfilled, (state, action) => {
                state.analytics.loading = false;
                // Map new API response structure to existing UI format
                if (action.payload && typeof action.payload === 'object') {
                    const payload = action.payload;
                    const practiceSummary = payload.practiceSummary || {};
                    const clinicianBreakdown = payload.clinicianBreakdown || [];

                    // Map clinician breakdown to expected format
                    const mappedClinicians = clinicianBreakdown.map((clinician: any) => ({
                        id: clinician.clinicianId || '',
                        clinicianId: clinician.clinicianId || '',
                        name: clinician.clinicianName || '',
                        title: clinician.title || '',
                        specialty: Array.isArray(clinician.specialties) ? clinician.specialties.join(', ') : (clinician.specialties || ''),
                        metrics: {
                            profileViews: {
                                total: clinician.metrics?.profileViews || 0,
                                trend: '0%',
                                trendValue: 0
                            },
                            profileClicks: {
                                total: clinician.metrics?.profileClicks || 0,
                                trend: '0%',
                                trendValue: 0
                            },
                            connectRequests: {
                                total: clinician.metrics?.leads || 0,
                                trend: '0%',
                                trendValue: 0
                            },
                            conversionRates: {
                                clickConversion: `${clinician.metrics?.clickThroughRate || 0}%`,
                                connectionConversion: `${clinician.metrics?.conversionRate || 0}%`,
                                overallConversion: `${clinician.metrics?.conversionRate || 0}%`
                            }
                        }
                    }));

                    // Map to expected UI structure
                    state.analytics.data = {
                        data: {
                            data: mappedClinicians
                        },
                        metadata: {
                            dataPoints: {
                                views: practiceSummary.totalViews || 0,
                                clicks: practiceSummary.totalClicks || 0,
                                connections: practiceSummary.totalLeads || 0
                            },
                            generatedAt: new Date().toISOString(),
                            reportType: "therapist_analytics"
                        },
                        success: true,
                        summary: {
                            totalClinicians: payload.totalClinicians || mappedClinicians.length,
                            totalViews: practiceSummary.totalViews || 0,
                            totalClicks: practiceSummary.totalClicks || 0,
                            totalConnections: practiceSummary.totalLeads || 0,
                            totalLeads: practiceSummary.totalLeads || 0,
                            averageConversionRate: `${practiceSummary.conversionRate || 0}`
                        }
                    };
                } else {
                    state.analytics.data = null;
                }
                state.analytics.error = null;
            })
            .addCase(fetchClinicianProfileAnalytics.rejected, (state, action) => {
                state.analytics.loading = false;
                state.analytics.error = action.error.message || 'Failed to fetch profile analytics';
            })
            .addCase(inviteSimplePracticeClinician.fulfilled, (state) => {
                // no-op; could mark invited flag here if needed
            })
            .addCase(inviteSimplePracticeClinicianBulk.fulfilled, (state) => {
                // no-op
            })
            .addCase(sendBulkInvitation.pending, (state) => {
                state.status = 'loading';
                state.isLoading = true;
            })
            .addCase(sendBulkInvitation.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.isLoading = false;
                // Update invitation status for successful invites
                action.payload.successful_invites?.forEach((invite: any) => {
                    const clinician = state.practiceClinicians.find(c => c._id === invite.clinician_id);
                    if (clinician) {
                        clinician.invitationStatus = 'Invited';
                    }
                });
            })
            .addCase(sendBulkInvitation.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to send bulk invitations';
                state.isLoading = false;
            })
            // Activate clinicians cases
            .addCase(activateClinicians.pending, (state) => {
                state.activateClinicians.isLoading = true;
                state.activateClinicians.error = null;
                state.activateClinicians.activated = null;
            })
            .addCase(activateClinicians.fulfilled, (state, action) => {
                state.activateClinicians.isLoading = false;
                state.activateClinicians.activated = action.payload.results?.activated || 0;
                state.activateClinicians.error = null;
            })
            .addCase(activateClinicians.rejected, (state, action) => {
                state.activateClinicians.isLoading = false;
                state.activateClinicians.error = typeof action.payload === 'string'
                    ? action.payload
                    : action.error.message || 'Failed to activate clinicians';
                state.activateClinicians.activated = null;
            });
    },
});

export const { clearBulkUpdateState } = clinicSlice.actions;
export default clinicSlice.reducer;
